import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ILinea, ITipoOpcion } from './interfaces/linea.interface';
import { LineaService } from './services/linea.service';
import { MatDialog } from '@angular/material/dialog';
import { AbcEmpresasCustomizablesComponent } from '@p-parametria/mantenimiento/abc-empresas/components/abc-empresas-customizables/abc-empresas-customizables.component';

@Component({
  selector: 'app-linea-form',
  templateUrl: './linea-form.component.html',
  providers: [MessageService]
})
export class LineaFormComponent implements OnInit, OnChanges {
  @Input() model: ILinea | null = null;
  @Output() onSave = new EventEmitter<{ success: boolean; message: string }>();
  form!: FormGroup;
  loading: boolean = false;

  // Opciones para el dropdown de tipo
  tipoOptions: { label: string; value: string }[] = [];

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private lineaService: LineaService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadTiposOpciones();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model']) {
      if (changes['model'].currentValue) {
        this.loadLinea();
      } else {
        this.initForm();
      }
    }
  }

  initForm(): void {
    this.form = this.fb.group({
      numero_linea: [{ value: '', disabled: !!this.model }, [Validators.required]],
      descripcion: ['', [Validators.required]],
      tipo: ['', [Validators.required]]
    });
  }

  loadTiposOpciones(): void {
    this.lineaService.getTiposOpciones().subscribe({
      next: (response) => {
        if (response.success) {
          // Mapear las opciones del servidor al formato del dropdown
          this.tipoOptions = response.data.map((opcion: ITipoOpcion) => ({
            label: opcion.sOpcion,
            value: opcion.sValor
          }));
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron cargar las opciones de tipo'
          });
        }
      },
      error: (error) => {
        console.error('Error al cargar tipos:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar las opciones de tipo'
        });
      }
    });
  }

  loadLinea(): void {
    if (!this.model?.numero_linea) return;
    
    this.loading = true;
    // TODO: Implementar carga de línea cuando se tenga el servicio
    setTimeout(() => {
      this.form.patchValue({
        numero_linea: this.model?.numero_linea,
        descripcion: this.model?.descripcion,
        tipo: this.model?.tipo
      });
      this.form.get('numero_linea')?.disable();
      this.loading = false;
    }, 500);
  }

  create(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    this.loading = true;
    const formData = this.form.getRawValue();
    
    this.lineaService.createLinea(formData.numero_linea, formData.descripcion, formData.tipo).subscribe({
      next: (response) => {
        if (response.success || (response.data?.[0]?.error === "0")) {
          this.onSave.emit({
            success: true,
            message: 'Línea creada correctamente'
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.data?.[0]?.error || 'Error al crear la línea'
          });
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al crear la línea'
        });
        this.loading = false;
      }
    });
  }

  update(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    this.loading = true;
    const formData = this.form.getRawValue();
    
    this.lineaService.updateLinea(formData.numero_linea, formData.descripcion, formData.tipo).subscribe({
      next: (response) => {
        if (response.success || (response.data?.[0]?.error === "0")) {
          this.onSave.emit({
            success: true,
            message: 'Línea actualizada correctamente'
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.data?.[0]?.error || 'Error al actualizar la línea'
          });
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al actualizar la línea'
        });
        this.loading = false;
      }
    });
  }

  openCustomizables(): void {
    this.dialog.open(AbcEmpresasCustomizablesComponent, {
      width: '1000px',
      height: '1000px',
      disableClose: true,
      panelClass: ['custom-dialog-zindex'],
      hasBackdrop: true,
      autoFocus: false,
      data: {
        sTabla: 'lineas',
        sClave: `${this.form.get('numero_linea')?.value}`,
        sNombre: this.form.get('descripcion')?.value,
        sTipo: 'Opcion'
      }
    });
  }
} 